import { Component, forwardRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  AsyncValidator,
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  NG_ASYNC_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validators
} from '@angular/forms';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';

import { subdomainValidator } from '../validators/subdomain-validator';
import { domainExistsValidator } from '../validators/domain-exists-validator';

import { CompanyResource } from '@app/shared/modules/resources/company.resource';
import { IFBDomainInputMode, EFBDomainInputMode } from './domain-input.model';
import { domainValidator } from '@app/modules/ui/validators/domain-validator';

@Component({
  selector: 'app-domain-input',
  templateUrl: './domain-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DomainInputComponent),
      multi: true
    },
    {
      provide: NG_ASYNC_VALIDATORS,
      useExisting: forwardRef(() => DomainInputComponent),
      multi: true
    }
  ],
  standalone: false
})
export class DomainInputComponent implements OnChanges, ControlValueAccessor, AsyncValidator {
  @Input() mode: IFBDomainInputMode = EFBDomainInputMode.SUBDOMAIN;
  onTouched: () => void;
  DomainInputForm = new UntypedFormGroup({
    domain: new UntypedFormControl('')
  });
  isFocused = false;

  constructor(private companyResource: CompanyResource) {}

  private _controlClasses: string[] = [];

  @Input() set controlClasses(value: string) {
    const classes = value.split(' ');
    this._controlClasses = [...classes];
  }

  @Input() set markAsDirty(value) {
    if (value) {
      this.domain.markAsDirty();
    }
  }

  get inputClasses(): string[] {
    const classes = [...this._controlClasses];

    if (this.domain.dirty && this.domain.invalid) {
      classes.push('has-error');
    }

    if (this.domain.pending) {
      classes.push('has-progress');
    }

    if (this.isFocused) {
      classes.push('__active');
    }

    return classes;
  }

  get domain(): AbstractControl {
    return this.DomainInputForm.get('domain');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.mode) {
      if (this.mode === EFBDomainInputMode.SUBDOMAIN) {
        this.domain.setValidators([Validators.required, Validators.maxLength(40), subdomainValidator()]);
      } else if (this.mode === EFBDomainInputMode.CUSTOM_DOMAIN) {
        this.domain.setValidators([Validators.required, Validators.maxLength(40), domainValidator()]);
      }

      this.domain.clearAsyncValidators();
      this.domain.setAsyncValidators(domainExistsValidator(this.companyResource, this.mode));

      // Trigger async validator and show error
      requestAnimationFrame(() => {
        this.domain.updateValueAndValidity();
        if (this.domain.value) {
          this.domain.markAsDirty();
        }
      });
    }
  }

  writeValue(value: string) {
    if (value) {
      this.domain.setValue(sanitizeValue(value));
    }
  }

  registerOnChange(fn: any) {
    this.domain.valueChanges.pipe(map(sanitizeValue)).subscribe(fn);
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    if (isDisabled) {
      this.domain.disable();
    } else {
      this.domain.enable();
    }
  }

  validate(control: AbstractControl) {
    if (this.domain.pending) {
      // Fix for combination of ControlValueAccessor and AsyncValidator.
      // https://stackoverflow.com/questions/59659259/reusable-angular-reactive-form-controlvalueaccessor-material-stepper-async
      setTimeout(() => control.updateValueAndValidity(), 500);
      return of({ pending: true });
    }

    return of(this.domain.valid ? null : this.domain.errors);
  }

  onBlur() {
    this.onTouched();
    this.isFocused = false;
  }

  onFocus() {
    this.isFocused = true;
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();

    const value = event.clipboardData.getData('text/plain');
    this.domain.setValue(sanitizeValue(value));
  }
}

// Make value lower case and remove URL protocol in case URL is pasting
function sanitizeValue(value: string) {
  return value.toLowerCase().replace(/(^\w+:|^)\/\//, '');
}
